import React, { FC, useCallback, useContext, useRef, useState } from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

import { SelectionListDropDown } from '../../shared/common/SelectionListDropDown';
import LibraryManagedProductGroupDropdown from '../../shared/common/LibraryManagedProductGroupDropdown';
import { Popover } from '../../shared/components/Popover/Popover';

import './SelectionlistActionButtons.scss';
import { useOrderLists } from '../../shared/common/useOrderLists';
import { Orderlist } from '../../../models/OrderList';
import { useLibraryManagedProductGroups } from '../../yourborrowbox/marketing/productgroups/api/useLibraryManagedProductGroupsFeaturePages';
import { DropdownContext } from '../../hooks/dropdownContext';
import { useTranslation } from 'react-i18next';
import { THEME_BLACK, THEME_PRIMARY, themeColor } from '../../styles/basics/colors';

const StLibraryManagedProductGroupDropdown = styled(LibraryManagedProductGroupDropdown)`
    .bulkActionDropdownTrigger {
        padding: 0;
        border: none;
        color: ${themeColor(THEME_BLACK)};

        &:hover {
            color: ${themeColor(THEME_PRIMARY)} !important;
            background-color: unset;

            .bulkActionDropdownIndicatorIcon,
            .bulkActionDropdownActionIcon {
                color: ${themeColor(THEME_PRIMARY)} !important;
            }
        }
    }

    .bulkActionDropdownActionIcon {
        color: black !important;
        font-size: 14px;
    }

    .bulkActionDropdownIndicatorIcon {
        font-size: 14px;
    }
`;

type Props = {
    readonly isCampaignProduct?: boolean;
    readonly onCopyProductToList?: (targetList: Orderlist) => void;
    readonly onMoveProductToList?: (targetList: Orderlist) => void;
    readonly onAddToGroup?: (productGroupId: number) => void;
    readonly showAddToGroup?: boolean;
    readonly hideListId?: string;
};

const SelectionlistActionButtons: FC<Props> = ({
    onCopyProductToList = async () => {},
    onMoveProductToList = async () => {},
    onAddToGroup,
    isCampaignProduct,
    showAddToGroup = true,
    hideListId
}) => {
    const { t } = useTranslation();
    const [showDropDown, setShowDropDown] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const { isExpandedContext } = useContext(DropdownContext);
    const hideDropDown = useCallback(() => {
        if (isExpandedContext) {
            return () => {};
        }
        setShowDropDown(false);
    }, [isExpandedContext]);

    const { orderlists, createNewList } = useOrderLists();
    const productGroups = useLibraryManagedProductGroups();
    const [loading, setLoading] = useState(false);

    const handleCopyToList = useCallback(
        async (list: Orderlist) => {
            setLoading(true);
            try {
                await onCopyProductToList(list);
            } finally {
                setShowDropDown(false);
                setLoading(false);
            }
        },
        [onCopyProductToList]
    );

    const handleMoveToList = useCallback(
        async (list: Orderlist) => {
            setLoading(true);
            try {
                await onMoveProductToList(list);
            } finally {
                setShowDropDown(false);
                setLoading(false);
            }
        },
        [onMoveProductToList]
    );

    const handleAddToGroup = useCallback(
        (productGroupId: number) => {
            setShowDropDown(false);
            onAddToGroup?.(productGroupId);
        },
        [onAddToGroup]
    );

    return (
        <div
            ref={anchorRef}
            className={classNames('default-dd-nav', 'expanded-dd', 'actions', 'fl-left', showDropDown && 'active')}
        >
            <a className="bt-dd" onClick={() => setShowDropDown(it => !it)}>
                <i className="material-icons up">more_horiz</i>
            </a>
            <Popover
                className="action-buttons-popover"
                visible={showDropDown}
                target={anchorRef}
                placement="bottom-start"
                gap={6}
                displacement={-15}
                ref={popoverRef}
                onClose={hideDropDown}
                portal={false}
            >
                <SelectionListDropDown
                    iconCode="copy-product"
                    label={t('selectionList.copyToList')}
                    hideListId={hideListId}
                    onCreateNewListClicked={createNewList}
                    lists={orderlists}
                    onListClick={handleCopyToList}
                    disabled={loading}
                />
                <SelectionListDropDown
                    iconCode="move-product"
                    label={t('selectionList.moveToList')}
                    hideListId={hideListId}
                    onCreateNewListClicked={createNewList}
                    lists={orderlists}
                    onListClick={handleMoveToList}
                    disabled={loading}
                />
                {showAddToGroup && onAddToGroup && !isCampaignProduct && (
                    <StLibraryManagedProductGroupDropdown
                        onListClick={handleAddToGroup}
                        productGroups={productGroups}
                        dataDisplayNameValueContainer="dropdownTrigger"
                    />
                )}
            </Popover>
        </div>
    );
};

export default SelectionlistActionButtons;
